import React, { useState, useEffect } from "react";
import "@/App.css";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import CookieBanner from "@/components/CookieBanner";
import { 
  Phone, 
  MapPin, 
  Clock, 
  ChevronDown, 
  Menu, 
  X,
  Sparkles,
  Heart,
  Shield,
  Baby,
  Smile,
  Activity,
  Stethoscope,
  Wrench,
  CheckCircle,
  Mail,
  User,
  MessageSquare,
  Send,
  Navigation,
  FileText,
  ShieldCheck,
  BadgeCheck
} from "lucide-react";

// Logo URL from uploaded PDF
const LOGO_URL = "https://customer-assets.emergentagent.com/job_site-modifier-22/artifacts/lrn1f4hv_dott.%20enrico%20bruni.pdf";

// Custom Logo SVG Component - Faithful reproduction of the original logo
// Green gradient stylized dental arch (arcata dentale)
const LogoIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 50 35" 
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="logoGradient" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#0D9488" />
        <stop offset="30%" stopColor="#14B8A6" />
        <stop offset="60%" stopColor="#10B981" />
        <stop offset="100%" stopColor="#047857" />
      </linearGradient>
    </defs>
    {/* Upper dental arch - smooth curved teeth */}
    <path 
      d="M3 28 C3 28, 4 18, 8 14 C10 12, 12 11, 14 12 C16 13, 16 16, 18 16 C20 16, 20 12, 22 11 C24 10, 26 10, 28 11 C30 12, 30 16, 32 16 C34 16, 34 13, 36 12 C38 11, 40 12, 42 14 C46 18, 47 28, 47 28" 
      stroke="url(#logoGradient)" 
      strokeWidth="4" 
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    {/* Individual tooth details */}
    <path d="M8 16 Q10 22, 12 16" stroke="url(#logoGradient)" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M14 14 Q16 21, 18 14" stroke="url(#logoGradient)" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M20 12 Q22 20, 24 12" stroke="url(#logoGradient)" strokeWidth="3" strokeLinecap="round" fill="none"/>
    <path d="M26 12 Q28 20, 30 12" stroke="url(#logoGradient)" strokeWidth="3" strokeLinecap="round" fill="none"/>
    <path d="M32 14 Q34 21, 36 14" stroke="url(#logoGradient)" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M38 16 Q40 22, 42 16" stroke="url(#logoGradient)" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
  </svg>
);

// WhatsApp SVG Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Data
const teamData = {
  medici: [
    {
      name: "Dott. Enrico Bruni",
      role: "Odontoiatra – Perfezionato in Ortodonzia e Pedodonzia",
      albo: "Iscrizione albo dei medici e degli odontoiatri di SV n° 464",
      description: "Il Dott. Enrico Bruni è un odontoiatra con formazione completa e approccio multidisciplinare, perfezionato in ortodonzia e pedodonzia. È Provider certificato Invisalign e gestisce casi ortodontici complessi con approccio digitale e progressivo. Ha completato il Corso Annuale di Parodontologia SIDP 2024 e si è formato in endodonzia e restaurativa avanzata.",
      specialties: ["Ortognatodonzia", "Invisalign", "Pedodonzia", "Conservativa", "Endodonzia", "Parodontologia"],
      isMain: true
    },
    {
      name: "Dott. Sergio Bruni",
      role: "Medico Chirurgo – Odontoiatra",
      albo: "Iscrizione Albo Medici Chirurghi di Genova n° 8949 | Odontoiatri n° 1102",
      description: "Con un'esperienza clinica maturata in oltre quarant'anni di attività, il Dott. Bruni ha operato con rigore scientifico e umanità. Professionista di riferimento per più generazioni di pazienti, è noto per la sua dedizione, la precisione clinica e l'approccio centrato sulla persona.",
      specialties: ["Medicina e Chirurgia", "Odontostomatologia"]
    },
    {
      name: "Dott.ssa Angelica D'Errico",
      role: "Odontoiatra – Specialista in Ortognatodonzia",
      albo: "Iscrizione Albo Odontoiatri di Potenza n° 316",
      description: "Laureata in Odontoiatria e Protesi Dentaria nel 2010, ha conseguito la specializzazione in Ortognatodonzia presso l'Università 'La Sapienza' di Roma nel 2016. Nella cura dei piccoli pazienti adotta un approccio empatico, attento e rassicurante.",
      specialties: ["Pedodonzia", "Ortodonzia"]
    },
    {
      name: "Dott. Daniele Pitto",
      role: "Odontoiatra – Master in Ortodonzia",
      albo: "Iscrizione Albo Odontoiatri di Genova n° 1671",
      description: "Laureato nel 2012, ha perfezionato la propria formazione frequentando il Master universitario in Ortodonzia presso l'Università di Roma Tor Vergata. La sua attività clinica è incentrata sull'ortodonzia dell'adulto e del bambino.",
      specialties: ["Ortodonzia", "Odontoiatria generale", "Prevenzione"]
    },
    {
      name: "Dott. Alan Trocino",
      role: "Odontoiatra",
      albo: "Iscrizione Albo Odontoiatri di Genova n° 1672",
      description: "Si occupa in particolare di restauri conservativi, terapie endodontiche e trattamenti parodontali mirati al mantenimento della salute orale e alla conservazione degli elementi dentari nel lungo termine.",
      specialties: ["Conservativa", "Endodonzia", "Parodontologia"]
    }
  ],
  assistenti: [
    {
      name: "Maria Lope Huerta",
      role: "ASO e Responsabile Segreteria",
      description: "Il volto accogliente dello studio: si occupa con efficienza e professionalità della segreteria, gestendo appuntamenti, accoglienza e comunicazione con i pazienti. È inoltre formata come assistente alla poltrona (ASO)."
    },
    {
      name: "Katia Vernazzano",
      role: "ASO – Assistente alla Poltrona",
      description: "Professionista esperta e affidabile, con una solida esperienza nell'ambito dell'assistenza alla poltrona e una competenza avanzata nella sterilizzazione degli strumenti. Garantisce standard elevati di sicurezza e igiene."
    }
  ]
};

const servicesData = [
  {
    id: 1,
    title: "Igiene orale professionale",
    icon: Sparkles,
    description: "Sedute professionali di detartrasi, levigatura radicolare e controllo della placca batterica. Utilizziamo strumenti ad ultrasuoni e manuali per garantire un'igiene profonda ma delicata.",
    image: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=400&h=300&fit=crop"
  },
  {
    id: 2,
    title: "Conservativa",
    icon: Shield,
    description: "Cura dei denti colpiti da carie o traumi. Utilizziamo materiali compositi estetici di ultima generazione, che si mimetizzano perfettamente con il colore naturale del dente.",
    image: null
  },
  {
    id: 3,
    title: "Endodonzia",
    icon: Activity,
    description: "Devitalizzazione con tecniche moderne, sistemi rotanti e strumenti elettronici per aumentare la precisione e ridurre i disagi. L'intervento è indolore e viene eseguito in anestesia locale.",
    image: null
  },
  {
    id: 4,
    title: "Prevenzione odontoiatrica",
    icon: Heart,
    description: "Controlli periodici, esami radiografici mirati e diagnosi precoce. Offriamo programmi personalizzati per adulti e bambini, comprendenti sigillature, fluorizzazioni e consulenza alimentare.",
    image: null
  },
  {
    id: 5,
    title: "Pedodonzia",
    icon: Baby,
    description: "Odontoiatria pediatrica con approccio dolce, empatico e rassicurante. Trattiamo carie, sigilliamo i solchi dei molari e monitoriamo l'eruzione dentale dei più piccoli.",
    image: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=400&h=300&fit=crop"
  },
  {
    id: 6,
    title: "Ortodonzia fissa e mobile",
    icon: Wrench,
    description: "Correggiamo malocclusioni con apparecchi ortodontici su misura. Ogni trattamento è preceduto da diagnosi dettagliata con radiografie, impronte e studio cefalometrico.",
    image: null
  },
  {
    id: 7,
    title: "Allineatori trasparenti",
    icon: Smile,
    description: "Mascherine trasparenti, rimovibili e su misura (Invisalign). Sono comode, igieniche e praticamente impercettibili, ideali per adulti e adolescenti.",
    image: "https://images.unsplash.com/photo-1609840113929-b130355987e1?w=400&h=300&fit=crop"
  },
  {
    id: 8,
    title: "Protesi fissa e mobile",
    icon: CheckCircle,
    description: "Corone, ponti e protesi removibili costruite su misura per garantire comfort e resa estetica. Collaboriamo con laboratori odontotecnici di fiducia.",
    image: null
  },
  {
    id: 9,
    title: "Implantologia dentale",
    icon: Stethoscope,
    description: "Sostituzione di denti mancanti con radici artificiali in titanio. L'intervento si esegue in anestesia locale con tecnologie digitali e chirurgia guidata.",
    image: null
  },
  {
    id: 10,
    title: "Parodontologia",
    icon: Heart,
    description: "Trattamento di gengiviti e parodontiti con terapie personalizzate. Offriamo programmi di mantenimento a lungo termine con controlli regolari.",
    image: null
  }
];

const locations = [
  {
    name: "Genova Voltri",
    address: "Via Ernesto Guala 2/1",
    cap: "16158 Genova",
    phone: "010 6136415",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2855.8!2d8.7477!3d44.4286!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12d34f2b8c8e8e8b%3A0x1234567890abcdef!2sVia%20Ernesto%20Guala%2C%202%2C%2016158%20Genova%20GE!5e0!3m2!1sit!2sit!4v1704067200000!5m2!1sit!2sit",
    coordinates: { lat: 44.4286, lng: 8.7477 }
  },
  {
    name: "Genova Foce",
    address: "Corso Torino 15/3",
    cap: "16129 Genova",
    phone: "010 564301",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2855.8!2d8.9477!3d44.4086!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12d34f2b8c8e8e8b%3A0x1234567890abcdef!2sCorso%20Torino%2C%2015%2C%2016129%20Genova%20GE!5e0!3m2!1sit!2sit!4v1704067200000!5m2!1sit!2sit",
    coordinates: { lat: 44.4086, lng: 8.9477 }
  }
];

// Components
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#chi-siamo", label: "Chi Siamo" },
    { href: "#servizi", label: "Servizi" },
    { href: "#tecnologie", label: "Tecnologie" },
    { href: "#sedi", label: "Sedi" },
    { href: "#contatti", label: "Contatti" }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-200/50' 
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-20">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a 
            href="#" 
            className="flex items-center flex-shrink-0"
            data-testid="logo-link"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img 
              src="/logo-header.png" 
              alt="Dott. Enrico Bruni - Ortodonzia Invisibile e Digitale" 
              className="h-9 md:h-10 w-auto"
              data-testid="logo-image"
            />
          </a>

          {/* Desktop Navigation - centered and spread */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-12">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-slate-600 hover:text-teal-700 transition-colors font-medium text-sm"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}-link`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          <a
            href="#contatti"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contatti')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="hidden lg:inline-flex bg-gradient-to-r from-teal-500 to-emerald-800 text-white rounded-full px-5 py-2 text-sm font-medium hover:shadow-lg hover:opacity-90 transition-all"
            data-testid="nav-prenota-btn"
          >
            Prenota Visita
          </a>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-slate-200/50" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block py-3 text-slate-600 hover:text-teal-700 transition-colors font-medium"
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}-link`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contatti"
              onClick={(e) => scrollToSection(e, '#contatti')}
              className="block mt-4 bg-gradient-to-r from-teal-500 to-emerald-800 text-white rounded-full px-6 py-3 font-medium text-center"
              data-testid="mobile-prenota-btn"
            >
              Prenota Visita
            </a>
          </nav>
        )}
      </div>
    </header>
  );
};

const Hero = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1609840113929-b130355987e1?w=1920&h=1080&fit=crop"
          alt="Paziente con sorriso perfetto grazie ad allineatori invisibili Invisalign - Studio Dentistico Dott. Bruni Genova"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-2xl">
          <span className="inline-block text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold mb-6">
            Studio Dentistico a Genova
          </span>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 font-medium font-['Outfit'] leading-tight mb-6">
            Dentista a Genova:<br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-teal-600 to-emerald-800">
              il tuo sorriso, la nostra passione
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl leading-relaxed text-slate-600 mb-10 max-w-xl">
            Studio dentistico del Dott. Enrico Bruni con due sedi a Genova. 
            Specializzati in ortodonzia invisibile Invisalign, implantologia e cure odontoiatriche personalizzate per tutta la famiglia.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#contatti"
              onClick={(e) => scrollToSection(e, '#contatti')}
              className="inline-flex items-center justify-center bg-gradient-to-r from-teal-500 to-emerald-800 text-white rounded-full px-8 py-4 font-medium hover:shadow-xl hover:opacity-90 transition-all"
              data-testid="hero-prenota-btn"
            >
              Prenota una Visita
            </a>
            <a
              href="#servizi"
              onClick={(e) => scrollToSection(e, '#servizi')}
              className="inline-flex items-center justify-center bg-slate-100 text-slate-900 rounded-full px-8 py-4 font-medium hover:bg-slate-200 transition-all"
              data-testid="hero-servizi-btn"
            >
              Scopri i Servizi
              <ChevronDown className="w-5 h-5 ml-2" />
            </a>
          </div>

          {/* Quick Info */}
          <div className="flex flex-wrap gap-6 mt-12 pt-8 border-t border-slate-200/50">
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-5 h-5 text-teal-600" />
              <span>2 Sedi a Genova</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Phone className="w-5 h-5 text-teal-600" />
              <span>347 102 1765</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-5 h-5 text-teal-600" />
              <span>Lun-Ven 9-19</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="chi-siamo" className="py-24 md:py-32 bg-white" data-testid="chi-siamo-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold">
            Chi Siamo
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900 font-medium font-['Outfit'] mt-4">
            Il team di odontoiatri<br />del tuo studio dentistico a Genova
          </h2>
        </div>

        {/* Intro with Image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1629909615184-74f495363b67?w=800&h=600&fit=crop"
              alt="Studio dentistico moderno a Genova con attrezzature all'avanguardia"
              className="w-full h-auto"
            />
          </div>
          <div>
            <h3 className="text-xl sm:text-2xl text-slate-900 font-medium font-['Outfit'] mb-6">
              Esperienza e innovazione per il tuo sorriso
            </h3>
            <p className="text-base sm:text-lg leading-relaxed text-slate-600 mb-6">
              Lo Studio Dentistico del Dott. Enrico Bruni rappresenta un punto di riferimento 
              a Genova per l'odontoiatria di qualità. Con due sedi strategicamente posizionate 
              a Voltri e Foce, offriamo cure odontoiatriche complete per tutta la famiglia.
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-slate-600">
              Il nostro team è composto da professionisti specializzati in diverse branche 
              dell'odontoiatria, pronti a offrirvi trattamenti personalizzati con tecnologie 
              all'avanguardia in un ambiente accogliente e rassicurante.
            </p>
          </div>
        </div>

        {/* Team - Medici */}
        <div className="mb-16">
          <h3 className="text-xl sm:text-2xl text-slate-900 font-medium font-['Outfit'] mb-8 text-center">
            I Nostri Medici
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamData.medici.map((member, index) => (
              <div
                key={index}
                className={`bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 ${
                  member.isMain ? 'lg:col-span-2 lg:row-span-2' : ''
                }`}
                data-testid={`team-member-${index}`}
              >
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r from-teal-500 to-emerald-800 flex items-center justify-center mb-6 ${member.isMain ? 'w-20 h-20' : ''}`}>
                  <User className={`text-white ${member.isMain ? 'w-10 h-10' : 'w-8 h-8'}`} />
                </div>
                <h4 className={`text-slate-900 font-medium font-['Outfit'] mb-2 ${member.isMain ? 'text-2xl' : 'text-lg'}`}>
                  {member.name}
                </h4>
                <p className="text-teal-700 text-sm font-medium mb-2">{member.role}</p>
                <p className="text-slate-500 text-xs mb-4">{member.albo}</p>
                <p className={`text-slate-600 leading-relaxed ${member.isMain ? 'text-base' : 'text-sm'}`}>
                  {member.description}
                </p>
                {member.specialties && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {member.specialties.slice(0, member.isMain ? 6 : 3).map((specialty, i) => (
                      <span 
                        key={i}
                        className="text-xs bg-teal-50 text-teal-700 px-3 py-1 rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Team - Assistenti */}
        <div>
          <h3 className="text-xl sm:text-2xl text-slate-900 font-medium font-['Outfit'] mb-8 text-center">
            Il Nostro Staff
          </h3>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamData.assistenti.map((member, index) => (
              <div
                key={index}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300"
                data-testid={`staff-member-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center mb-4">
                  <User className="w-7 h-7 text-slate-500" />
                </div>
                <h4 className="text-lg text-slate-900 font-medium font-['Outfit'] mb-1">
                  {member.name}
                </h4>
                <p className="text-teal-700 text-sm font-medium mb-3">{member.role}</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  return (
    <section id="servizi" className="py-24 md:py-32 bg-slate-50" data-testid="servizi-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold">
            I Nostri Servizi
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900 font-medium font-['Outfit'] mt-4">
            Tutti i servizi odontoiatrici<br />per il tuo sorriso
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 max-w-2xl mx-auto">
            Dal dentista a Genova per cure complete: dalla prevenzione all'ortodonzia invisibile,
            dall'implantologia alla pedodonzia, con tecnologie moderne e un approccio personalizzato.
          </p>
        </div>

        {/* Services Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service, index) => {
            const IconComponent = service.icon;
            const isLarge = service.image !== null;
            
            return (
              <div
                key={service.id}
                className={`bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 ${
                  isLarge ? 'lg:col-span-1 lg:row-span-2' : ''
                }`}
                data-testid={`service-${service.id}`}
              >
                {service.image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6 md:p-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-teal-500/10 to-emerald-500/10 flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg text-slate-900 font-medium font-['Outfit'] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tecnologie - Subsection */}
        <div className="mt-24" id="tecnologie">
          <div className="text-center mb-14">
            <span className="text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold">
              Le Nostre Tecnologie
            </span>
            <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900 font-medium font-['Outfit'] mt-4">
              Strumentazione all'avanguardia
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 max-w-2xl mx-auto">
              Investiamo costantemente nelle tecnologie più avanzate per offrirti diagnosi 
              più accurate, trattamenti più precisi e un'esperienza più confortevole.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologiesData.map((tech) => (
              <div
                key={tech.id}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300"
                data-testid={`technology-${tech.id}`}
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={tech.image}
                    alt={tech.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-3 left-3">
                    <span className="bg-white/90 backdrop-blur-sm text-teal-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {tech.subtitle}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg text-slate-900 font-medium font-['Outfit'] mb-2">
                    {tech.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {tech.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {tech.features.map((feature, i) => (
                      <span key={i} className="text-xs bg-teal-50 text-teal-700 px-3 py-1 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const technologiesData = [
  {
    id: 1,
    title: "CBCT Cone Beam",
    subtitle: "Tomografia Computerizzata 3D",
    description: "La CBCT (Cone Beam Computed Tomography) consente di ottenere immagini tridimensionali delle strutture ossee e dentali con una precisione straordinaria e una dose di radiazioni ridotta rispetto alla TAC tradizionale. È fondamentale per la pianificazione implantare, l'analisi di denti inclusi, la valutazione delle patologie ossee e la chirurgia guidata.",
    image: "https://images.pexels.com/photos/6501859/pexels-photo-6501859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["Immagini 3D ad alta risoluzione", "Bassa dose di radiazioni", "Pianificazione implantare di precisione"]
  },
  {
    id: 2,
    title: "RVG - Videoradiografia",
    subtitle: "Radiografia Digitale Endorale",
    description: "La videoradiografia (RVG) è il sistema di radiografia digitale endorale di ultima generazione. Rispetto alla pellicola tradizionale, offre immagini istantanee ad alta definizione con una riduzione fino all'80% della dose radiante. Consente di visualizzare immediatamente carie, granulomi, riassorbimenti radicolari e il livello osseo parodontale.",
    image: "https://images.unsplash.com/photo-1522849696084-818b29dfe210?w=600&h=400&fit=crop",
    features: ["Riduzione 80% dose radiazioni", "Immagini istantanee", "Alta definizione diagnostica"]
  },
  {
    id: 3,
    title: "Scanner Intraorale",
    subtitle: "Impronte Digitali 3D",
    description: "Lo scanner intraorale consente di acquisire impronte digitali tridimensionali della bocca del paziente in pochi minuti, eliminando le fastidiose paste da impronta tradizionali. Le impronte digitali sono più precise, più confortevoli e permettono di visualizzare in tempo reale il risultato, accelerando i tempi di realizzazione di protesi, allineatori e restauri.",
    image: "https://images.unsplash.com/photo-1626878880028-0438b1403b3f?w=600&h=400&fit=crop",
    features: ["Niente più pasta da impronta", "Precisione submillimetrica", "Risultato in tempo reale"]
  },
  {
    id: 4,
    title: "Videocamera Intraorale",
    subtitle: "Diagnostica Visiva Avanzata",
    description: "La videocamera intraorale permette di catturare immagini e video ad alta risoluzione direttamente all'interno della bocca. È uno strumento diagnostico prezioso che consente al paziente di vedere ciò che il dentista osserva, facilitando la comprensione della diagnosi e del piano di trattamento proposto.",
    image: "https://images.unsplash.com/photo-1656428964836-78d54bf76231?w=600&h=400&fit=crop",
    features: ["Immagini HD in tempo reale", "Coinvolgimento del paziente", "Documentazione clinica"]
  },
  {
    id: 5,
    title: "Microscopio Operatorio",
    subtitle: "Odontoiatria di Massima Precisione",
    description: "Il microscopio operatorio consente di lavorare con ingrandimenti fino a 25x, rendendo visibili dettagli altrimenti invisibili a occhio nudo. È utilizzato in endodonzia per individuare canali accessori, in conservativa per otturazioni di precisione e in chirurgia per interventi minimamente invasivi. Rappresenta lo standard più elevato di cura.",
    image: "https://images.pexels.com/photos/4687358/pexels-photo-4687358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: ["Ingrandimento fino a 25x", "Massima precisione operativa", "Standard elevato di cura"]
  }
];

const TechnologySection = () => {
  return (
    <section id="tecnologie" className="py-24 md:py-32 bg-white" data-testid="tecnologie-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold">
            Le Nostre Tecnologie
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900 font-medium font-['Outfit'] mt-4">
            Strumentazione all'avanguardia<br />per cure di eccellenza
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 max-w-2xl mx-auto">
            Investiamo costantemente nelle tecnologie più avanzate per offrirti diagnosi 
            più accurate, trattamenti più precisi e un'esperienza più confortevole.
          </p>
        </div>

        {/* Technologies */}
        <div className="space-y-12">
          {technologiesData.map((tech, index) => {
            const isReversed = index % 2 !== 0;
            
            return (
              <div
                key={tech.id}
                className={`grid lg:grid-cols-2 gap-8 items-center ${isReversed ? 'lg:direction-rtl' : ''}`}
                data-testid={`technology-${tech.id}`}
              >
                {/* Image */}
                <div className={`relative rounded-3xl overflow-hidden group ${isReversed ? 'lg:order-2' : ''}`}>
                  <img
                    src={tech.image}
                    alt={tech.title}
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                      {tech.subtitle}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className={`${isReversed ? 'lg:order-1' : ''}`}>
                  <h3 className="text-2xl sm:text-3xl text-slate-900 font-medium font-['Outfit'] mb-4">
                    {tech.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {tech.description}
                  </p>
                  
                  {/* Features */}
                  <div className="space-y-3">
                    {tech.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-4 h-4 text-teal-600" />
                        </div>
                        <span className="text-slate-700 font-medium text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const LocationsSection = () => {
  return (
    <section id="sedi" className="py-24 md:py-32 bg-slate-50" data-testid="sedi-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold">
            Le Nostre Sedi
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900 font-medium font-['Outfit'] mt-4">
            Due studi dentistici a Genova<br />Voltri e Foce
          </h2>
        </div>

        {/* Locations Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {locations.map((location, index) => (
            <div
              key={index}
              className="bg-slate-50 border border-slate-200 rounded-3xl overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300"
              data-testid={`location-${index}`}
            >
              {/* Google Maps Embed */}
              <div className="h-64 bg-slate-200 relative overflow-hidden">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(location.address + ', ' + location.cap)}&zoom=16`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Mappa ${location.name}`}
                  className="w-full h-full"
                  data-testid={`map-iframe-${index}`}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/80 to-transparent p-4">
                  <h3 className="text-xl font-medium font-['Outfit'] text-white">{location.name}</h3>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-teal-600 mt-0.5" />
                    <div>
                      <p className="text-slate-900 font-medium">{location.address}</p>
                      <p className="text-slate-600">{location.cap}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-teal-600" />
                    <a 
                      href={`tel:${location.phone.replace(/\s/g, '')}`}
                      className="text-slate-900 hover:text-teal-700 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address + ', ' + location.cap)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center bg-slate-100 text-slate-900 rounded-full px-4 py-3 font-medium hover:bg-slate-200 transition-all text-sm"
                    data-testid={`location-${index}-map-btn`}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Google Maps
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.address + ', ' + location.cap)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center bg-gradient-to-r from-teal-500 to-emerald-800 text-white rounded-full px-4 py-3 font-medium hover:opacity-90 transition-all text-sm"
                    data-testid={`location-${index}-directions-btn`}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Indicazioni
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Info Banner */}
        <div className="mt-12 bg-gradient-to-r from-teal-600 to-emerald-800 rounded-3xl p-8 md:p-12 text-white">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-medium font-['Outfit'] mb-2">
                Hai bisogno di informazioni?
              </h3>
              <p className="text-teal-100">
                Contattaci telefonicamente o via WhatsApp per prenotare un appuntamento
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3">
              <a
                href="tel:3471021765"
                className="inline-flex items-center justify-center bg-white text-teal-700 rounded-full px-6 py-3 font-medium hover:bg-teal-50 transition-all"
                data-testid="call-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                347 102 1765
              </a>
              <a
                href="https://wa.me/393471021765"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-[#25D366] text-white rounded-full px-6 py-3 font-medium hover:bg-[#20BD5A] transition-all"
                data-testid="whatsapp-banner-btn"
              >
                <WhatsAppIcon className="w-4 h-4 mr-2" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    
    setTimeout(() => setSubmitSuccess(false), 5000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contatti" className="py-24 md:py-32 bg-slate-50" data-testid="contatti-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] text-teal-700 font-semibold">
            Contattaci
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900 font-medium font-['Outfit'] mt-4">
            Prenota la tua visita odontoiatrica
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-slate-600 mt-4 max-w-2xl mx-auto">
            Contatta lo studio dentistico del Dott. Enrico Bruni a Genova. 
            Compila il modulo o scrivici su WhatsApp per prenotare un appuntamento.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white border border-slate-200 rounded-3xl p-8 md:p-10">
            {submitSuccess ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-medium font-['Outfit'] text-slate-900 mb-2">
                  Messaggio inviato!
                </h3>
                <p className="text-slate-600">
                  Ti contatteremo al più presto.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Nome e Cognome *
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all outline-none"
                        placeholder="Mario Rossi"
                        data-testid="contact-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all outline-none"
                        placeholder="mario.rossi@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Telefono
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all outline-none"
                        placeholder="+39 333 123 4567"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Messaggio *
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all outline-none resize-none"
                        placeholder="Descrivi la tua richiesta o i tuoi sintomi..."
                        data-testid="contact-message-input"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-teal-500 to-emerald-800 text-white rounded-full px-8 py-4 font-medium hover:shadow-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <span>Invio in corso...</span>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Invia Richiesta
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h3 className="text-xl font-medium font-['Outfit'] text-slate-900 mb-6">
                Informazioni di Contatto
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Dott. Enrico Bruni</h4>
                  <p className="text-slate-600 text-sm">Odontoiatra</p>
                  <p className="text-slate-500 text-xs mt-1">P.IVA: 02295490995 | Albo Savona n° 464</p>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="w-5 h-5 text-teal-600" />
                    <div>
                      <a href="tel:3471021765" className="text-slate-900 hover:text-teal-700 transition-colors font-medium">
                        347 102 1765
                      </a>
                      <span className="text-slate-400 mx-2">|</span>
                      <a href="tel:0106136415" className="text-slate-900 hover:text-teal-700 transition-colors">
                        010 6136415
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-teal-600" />
                    <a href="mailto:studiodrbruni@gmail.com" className="text-slate-900 hover:text-teal-700 transition-colors">
                      studiodrbruni@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h3 className="text-xl font-medium font-['Outfit'] text-slate-900 mb-6">
                Orari di Apertura
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Lunedì - Venerdì</span>
                  <span className="text-slate-900 font-medium">9:00 - 19:00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Sabato</span>
                  <span className="text-slate-900 font-medium">Su appuntamento</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Domenica</span>
                  <span className="text-slate-500">Chiuso</span>
                </div>
              </div>
            </div>

            <a
              href="https://wa.me/393471021765"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full bg-[#25D366] text-white rounded-2xl px-8 py-5 font-medium hover:bg-[#20BD5A] transition-all"
              data-testid="contact-whatsapp-btn"
            >
              <WhatsAppIcon className="w-6 h-6" />
              <span>Scrivici su WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ onOpenPrivacy }) => {
  return (
    <footer className="bg-slate-900 text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/logo-header.png" 
                alt="Dott. Enrico Bruni" 
                className="h-12 w-auto brightness-0 invert opacity-90"
                data-testid="footer-logo"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              Due sedi a Genova per prenderci cura della tua salute orale. 
              Ortodonzia invisibile, implantologia e cure personalizzate per tutta la famiglia.
            </p>
          </div>

          {/* Sedi */}
          <div>
            <h4 className="font-medium font-['Outfit'] mb-4">Le Nostre Sedi</h4>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-white">Genova Voltri</p>
                <p className="text-slate-400">Via Ernesto Guala 2/1</p>
                <p className="text-slate-400">16158 Genova</p>
              </div>
              <div>
                <p className="text-white">Genova Foce</p>
                <p className="text-slate-400">Corso Torino 15/3</p>
                <p className="text-slate-400">16129 Genova</p>
              </div>
            </div>
          </div>

          {/* Contatti */}
          <div>
            <h4 className="font-medium font-['Outfit'] mb-4">Contatti</h4>
            <div className="space-y-3 text-sm">
              <a href="tel:3471021765" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                347 102 1765
              </a>
              <a href="tel:0106136415" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                010 6136415
              </a>
              <a href="mailto:studiodrbruni@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                studiodrbruni@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Informazioni Legali */}
        <div className="py-8 border-t border-slate-800 mb-8">
          <h4 className="font-medium font-['Outfit'] text-slate-300 mb-6 text-center">Informazioni Legali</h4>
          <div className="grid md:grid-cols-3 gap-6">
            
            {/* Autorizzazione Sanitaria */}
            <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700/50" data-testid="autorizzazione-sanitaria">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <BadgeCheck className="w-5 h-5 text-teal-400" />
                </div>
                <h5 className="text-white font-medium text-sm font-['Outfit']">Autorizzazione Sanitaria</h5>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed">
                Autorizzazione Sanitaria n° <span className="text-slate-300 font-medium">3412</span><br />
                Ordine dei Medici di Genova
              </p>
            </div>

            {/* RC Professionale */}
            <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700/50" data-testid="rc-professionale">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-teal-400" />
                </div>
                <h5 className="text-white font-medium text-sm font-['Outfit']">RC Professionale</h5>
              </div>
              <div className="text-slate-400 text-xs leading-relaxed space-y-1">
                <p>Compagnia: <span className="text-slate-300">Assicuratrice Milanese S.p.A.</span></p>
                <p>Polizza: <span className="text-slate-300">RC verso terzi - Medico Odontoiatra</span></p>
                <p>N° Polizza: <span className="text-slate-300">1300291268167</span></p>
                <p>Ramo/Rischio: <span className="text-slate-300">013504</span></p>
                <p>Validità: <span className="text-slate-300">17/01/2026 - 17/01/2027</span></p>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700/50" data-testid="privacy-policy-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-teal-500/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-400" />
                </div>
                <h5 className="text-white font-medium text-sm font-['Outfit']">Privacy e Cookie</h5>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed mb-3">
                Informativa sul trattamento dei dati personali ai sensi del GDPR (UE) 2016/679
              </p>
              <button
                onClick={onOpenPrivacy}
                className="text-teal-400 text-xs font-medium hover:text-teal-300 transition-colors underline underline-offset-2"
                data-testid="privacy-policy-link"
              >
                Leggi la Privacy Policy completa
              </button>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Studio Dentistico Dott. Enrico Bruni. Tutti i diritti riservati.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-slate-500 text-sm mb-16 md:mb-0">
            <span>P.IVA: 02295490995 | C.F.: BRNNRC88B14D969S</span>
            <span className="hidden sm:inline">|</span>
            <button
              onClick={onOpenPrivacy}
              className="hover:text-slate-300 transition-colors underline underline-offset-2"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/393471021765"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform whatsapp-btn"
      data-testid="whatsapp-button"
      aria-label="Contattaci su WhatsApp"
    >
      <WhatsAppIcon className="w-8 h-8" />
    </a>
  );
};

function App() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  return (
    <div className="App">
      <Header />
      <main>
        <Hero />
        <AboutSection />
        <ServicesSection />
        <LocationsSection />
        <ContactSection />
      </main>
      <Footer onOpenPrivacy={() => setIsPrivacyOpen(true)} />
      <WhatsAppButton />
      <CookieBanner />
      <PrivacyPolicy isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
    </div>
  );
}

export default App;
